import { FormEvent, useState } from "react";
import { Plus, Vault } from "lucide-react";
import * as magazzinoApi from "../../api/magazzino";
import * as catalogoApi from "../../api/catalogo";
import { DispositivoCatalogo } from "../../api/catalogo";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { DispositivoAutocomplete } from "../../components/ui/DispositivoAutocomplete";
import { SedeAutocomplete } from "../../components/ui/SedeAutocomplete";
import { etichettaMatricola } from "../../utils/matricola";

/**
 * «Aggiungi cassaforte» — il caricamento semplice del magazzino.
 *
 * Quattro dati e uno solo obbligatorio: marca e modello, che vengono dal
 * Catalogo Dispositivi (una coppia sola per modello, già univoca lì) invece di
 * due caselle di testo che lo duplicherebbero. Se il modello non c'è ancora si
 * aggiunge da qui, con la stessa rotta della pagina Catalogo.
 *
 * Matricola, sede e data di consegna possono mancare, e mancando restano NULL:
 * una cassaforte entra a scaffale anche prima che se ne conosca il seriale o il
 * punto vendita. Le regole vere le applica `POST /api/magazzino/casseforti`.
 */
export function CassaforteForm({ onChiudi, onFatto }: { onChiudi: () => void; onFatto: () => void }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const puoAggiungereModello = hasPermission("catalogo:write");

  const [modelloTesto, setModelloTesto] = useState("");
  const [modello, setModello] = useState<DispositivoCatalogo | null>(null);
  const [nuovoModello, setNuovoModello] = useState<{ marca: string; modello: string } | null>(null);
  const [salvaModelloInCorso, setSalvaModelloInCorso] = useState(false);

  const [matricola, setMatricola] = useState("");
  const [sedeTesto, setSedeTesto] = useState("");
  const [sedeId, setSedeId] = useState("");
  const [consegna, setConsegna] = useState("");

  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  function scegliModello(d: DispositivoCatalogo) {
    setModello(d);
    setModelloTesto(`${d.marca} ${d.modello}`);
  }

  async function aggiungiModello() {
    if (!nuovoModello) return;
    const marca = nuovoModello.marca.trim();
    const nome = nuovoModello.modello.trim();
    if (!marca || !nome) {
      setErrore("Per aggiungere un modello servono marca e modello.");
      return;
    }
    setSalvaModelloInCorso(true);
    setErrore(null);
    try {
      const { dispositivo } = await catalogoApi.createDispositivo({ tipo_dispositivo: "Cassaforte", marca, modello: nome });
      scegliModello(dispositivo);
      setNuovoModello(null);
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile aggiungere il modello al catalogo."));
    } finally {
      setSalvaModelloInCorso(false);
    }
  }

  async function salva(e: FormEvent) {
    e.preventDefault();
    if (!modello) {
      setErrore("Marca e modello sono obbligatori: scegli il modello dal Catalogo, o aggiungilo.");
      return;
    }
    setInCorso(true);
    setErrore(null);
    try {
      const { record } = await magazzinoApi.creaCassaforte({
        dispositivo_catalogo_id: modello.id,
        matricola: matricola.trim() || null,
        sede_id: sedeId || null,
        data_consegna_prevista: consegna || null,
      });
      showToast(
        `Cassaforte ${modello.marca} ${modello.modello} (${etichettaMatricola(record.matricola)}) caricata in magazzino.`,
        "success"
      );
      onFatto();
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile caricare la cassaforte."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modal
      open
      onClose={onChiudi}
      title="Aggiungi cassaforte"
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button type="submit" form="form-cassaforte-magazzino" loading={inCorso}>
            <Vault className="h-4 w-4" aria-hidden="true" />
            Carica in magazzino
          </Button>
        </div>
      }
    >
      <form id="form-cassaforte-magazzino" onSubmit={salva} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <DispositivoAutocomplete
            label="Marca e modello *"
            tipoDispositivo="Cassaforte"
            placeholder="Cerca nel Catalogo, es. Yougo U-ONE"
            value={modelloTesto}
            onChange={(v) => {
              setModelloTesto(v);
              // Chi corregge il testo dopo aver scelto non deve tenersi il
              // modello di prima: salverebbe una cassaforte diversa da quella scritta.
              setModello(null);
            }}
            onSelect={scegliModello}
          />
          {modello ? (
            <p className="text-xs text-ink/60">
              Marca <strong>{modello.marca}</strong> · Modello <strong>{modello.modello}</strong>
            </p>
          ) : (
            puoAggiungereModello &&
            !nuovoModello && (
              <button
                type="button"
                onClick={() => setNuovoModello({ marca: "", modello: "" })}
                className="flex items-center gap-1 self-start text-xs font-medium text-primary hover:underline"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Nuovo modello
              </button>
            )
          )}
        </div>

        {nuovoModello && !modello && (
          <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface/50 p-3">
            <p className="text-xs text-ink/60">Il modello viene aggiunto al Catalogo Dispositivi come cassaforte.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Marca"
                value={nuovoModello.marca}
                onChange={(e) => setNuovoModello({ ...nuovoModello, marca: e.target.value })}
              />
              <Input
                label="Modello"
                value={nuovoModello.modello}
                onChange={(e) => setNuovoModello({ ...nuovoModello, modello: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setNuovoModello(null)}>
                Annulla
              </Button>
              <Button type="button" size="sm" loading={salvaModelloInCorso} onClick={() => void aggiungiModello()}>
                Aggiungi al Catalogo
              </Button>
            </div>
          </div>
        )}

        <Input
          label="Matricola"
          placeholder="Es. UB120004"
          hint="Facoltativa: se non è ancora nota lasciala vuota e impostala dopo. Una volta scritta non si cambia più."
          value={matricola}
          onChange={(e) => setMatricola(e.target.value)}
        />

        <SedeAutocomplete
          label="Sede"
          placeholder="Facoltativa — cerca per insegna, nome o città"
          value={sedeTesto}
          onChange={setSedeTesto}
          sedeId={sedeId}
          onSedeIdChange={setSedeId}
          soloSelezione
        />

        <Input
          type="date"
          label="Consegna prevista"
          hint="Arrivo presunto in magazzino dal fornitore. Facoltativa."
          value={consegna}
          onChange={(e) => setConsegna(e.target.value)}
        />

        {errore && <p className="text-sm font-medium text-accent">{errore}</p>}
      </form>
    </Modal>
  );
}
